import {TooltipNumberOfRunsOptions} from '../WorkspaceDrawer/Settings/types';
import {type MarkSeriesPointForCrosshair} from './crosshairUtils';
import {getNeighborsIndexRange} from './utils';

/**
 * Check if a crosshair value matches the highlighted run.
 * In multi-metric mode, also requires the metric to match.
 */
export function isHighlightMatch(
  val: MarkSeriesPointForCrosshair,
  highlightRun: string | undefined,
  highlightMetric: string | symbol | undefined,
  isMultiMetric: boolean
): boolean {
  const runMatch = val.uniqueId === highlightRun;
  if (isMultiMetric && highlightMetric != null) {
    return runMatch && val.metricName === highlightMetric;
  }
  return runMatch;
}

type CrosshairRowSelectionOptions = {
  highlightRun?: string;
  highlightMetric?: string | symbol;
  isMultiMetric: boolean;
  isHoveredPanel: boolean;
  highlightedCompanionRunOnly: boolean;
  tooltipNumberOfRuns: TooltipNumberOfRunsOptions;
};

/**
 * Select rows from values already sorted by descending y. Sorting stays cached
 * separately in the caller so changing the highlighted run does not sort again.
 * Selected rows retain their original point objects.
 */
export function getCrosshairRowSelection(
  sortedValues: MarkSeriesPointForCrosshair[],
  {
    highlightRun,
    highlightMetric,
    isMultiMetric,
    isHoveredPanel,
    highlightedCompanionRunOnly,
    tooltipNumberOfRuns,
  }: CrosshairRowSelectionOptions
) {
  let minimumNeighbors = 0;
  if (!isHoveredPanel && highlightedCompanionRunOnly) {
    minimumNeighbors = 0;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.Single) {
    minimumNeighbors = 0;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.Default) {
    minimumNeighbors = 4;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.All) {
    minimumNeighbors = Infinity;
  }

  const matchedIndex = sortedValues.findIndex(v =>
    isHighlightMatch(v, highlightRun, highlightMetric, isMultiMetric)
  );

  const [minIndex, maxIndex] = getNeighborsIndexRange({
    neighborsPerSide: minimumNeighbors,
    list: sortedValues,
    index: matchedIndex,
  });

  /**
   * We don't know if we're in multiple metrics mode so we gather the data to handle either in a single loop
   * Filtered: these are the items to display based on neighboring rules
   * Matched: the hovered run has multiple metrics logged, so we don't show neighbors we just show the exact matches
   *
   * Note: there is a design discussion in progress about how to handle multiple metrics. We experimented with showing just the matching run in the overlay (reverted here: https://github.com/wandb/core/pull/20659) but a customer asked for the revert. Leaving this here for now as we will revisit in the future.
   */
  const {filteredValues, matchedValues} = sortedValues.reduce(
    (acc, val, index) => {
      // Match on uniqueId (and metric in multi-metric mode)
      if (isHighlightMatch(val, highlightRun, highlightMetric, isMultiMetric)) {
        acc.matchedValues.push(val);
        acc.filteredValues.push(val);
        return acc;
      }

      // Show if within the neighbor range
      if (index >= minIndex && index <= maxIndex) {
        acc.filteredValues.push(val);
      }

      return acc;
    },
    {
      filteredValues: [] as MarkSeriesPointForCrosshair[],
      matchedValues: [] as MarkSeriesPointForCrosshair[],
    }
  );

  // Check if baseline exists but is outside the neighbor range
  const baselineIndex = sortedValues.findIndex(v => v.isBaseline);
  const baselineValue =
    baselineIndex >= 0 ? sortedValues[baselineIndex] : undefined;
  const baselineInFilteredValues = filteredValues.some(v => v.isBaseline);
  const showBaselineSeparately = baselineValue && !baselineInFilteredValues;

  // Determine if baseline is above (high value) or below (low value) the visible range
  const baselineAboveRange = baselineIndex >= 0 && baselineIndex < minIndex;
  const baselineBelowRange = baselineIndex > maxIndex;

  let sanitizedNeighbors = filteredValues;
  if (baselineValue && showBaselineSeparately) {
    const baselineId = baselineValue.uniqueId ?? baselineValue.title ?? '';
    sanitizedNeighbors = filteredValues.filter(neighbor => {
      const neighborId = neighbor.uniqueId ?? neighbor.title ?? '';
      // Drop the baseline from the neighbor list when rendered separately.
      return neighborId !== baselineId;
    });
  }

  let neighborsToDisplay = sanitizedNeighbors;
  if (
    showBaselineSeparately &&
    baselineValue &&
    sanitizedNeighbors.length > 1
  ) {
    if (baselineAboveRange) {
      neighborsToDisplay = sanitizedNeighbors.slice(1);
    } else if (baselineBelowRange) {
      neighborsToDisplay = sanitizedNeighbors.slice(
        0,
        sanitizedNeighbors.length - 1
      );
    }
  }

  const valuesToRender = showBaselineSeparately
    ? neighborsToDisplay
    : filteredValues;

  const showGradients =
    sortedValues.length > valuesToRender.length && // gradients can't be active unless there are greater than nine runs
    valuesToRender.length > 5;

  /**
   * note: the maximum y values will have the lowest indexes because they sort in descending order. The values need to be sorted because in the plot the lines will be ordered from highest value (top of chart) to lowest value (bottom of chart) at any given x value and the overlay needs to match.
   */
  const showHiddenMaxValuesGradient = showGradients && minIndex > 0;
  const showHiddenMinValuesGradient =
    showGradients && maxIndex < sortedValues.length - 1;

  return {
    baselineValue,
    baselineAboveRange,
    baselineBelowRange,
    showBaselineSeparately,
    neighborsToDisplay,
    valuesToRender,
    matchedValueCount: matchedValues.length,
    visibleValueCount: valuesToRender.length,
    showHiddenMaxValuesGradient,
    showHiddenMinValuesGradient,
  };
}
